import React, { useState, useEffect, useMemo, useCallback, useRef, createContext, useContext } from "react";

// Context for sharing role data with child tab components (MasterSheet, IncentiveSheet)
export const IncentiveRoleContext = createContext({ availableRoles: [], moduleName: "" });
export const useIncentiveRoles = () => useContext(IncentiveRoleContext);
import {
  Button,
  InfoCardNewAdv,
  InfoWrapper,
  Modal,
  NewTab,
  showToast
} from "../../../../../components";
import TableIncentive from "./TableIncentive";
import { fetchRoles, fetchIncentiveCount } from "../../../../../redux/slices/incentives/incentivesApi";
import { fetchIncentiveSettingsApi, updateActiveRoles } from "../../../../../redux/slices/incentives/incentiveSettingApi";

// Constants moved outside component
// Module 1 = Global/All roles (visible across all incentive modules)
const MODULE_MAP = {
  "Bench": [1, 4],
  "IT": [1, 2, 3],
  "NonIT": [1, 2, 3]
};

const SETTINGS_KEY_MAP = {
  "Bench": "INCENTIVE_BENCH",
  "IT": "INCENTIVE_IT",
  "NonIT": "INCENTIVE_NON_IT"
};

const IncentiveModuleLayout = ({ 
  moduleName, 
  initialCards = [], 
  tabs = [],
  tabId
}) => {
  // Modal State
  const [selectedModal, setSelectedModal] = useState(null);
  const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);

  // Checked Roles State
  const [checkedRoleIds, setCheckedRoleIds] = useState(new Set());

  // Role Caching State
  const [availableRoles, setAvailableRoles] = useState([]);
  const [rolesLoaded, setRolesLoaded] = useState(false);

  // Custom Cards State
  const [customCards, setCustomCards] = useState([]);

  //kpi loading state
  const [kpiLoading,setKpiLoading]=useState(false)

  // Member Counts State
  const [memberCounts, setMemberCounts] = useState({});

  // Refs for optimization
  const settingUiidRef = useRef(null);
  const tableRef = useRef(null);
  const hasRestoredRef = useRef(false);
  const fetchingCountsRef = useRef(false);
  const countsAreFreshRef = useRef(false);
  const mountedRef = useRef(true);
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [isAddMemberPanelOpen, setIsAddMemberPanelOpen] = useState(false);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Fetch Roles ONCE on Mount
  useEffect(() => {
    const loadRoles = async () => {
      try {
        const rolesData = await fetchRoles({});
        if (!mountedRef.current) return;
        
        const roles = Array.isArray(rolesData) ? rolesData : rolesData.data || [];
        setAvailableRoles(roles);
        setRolesLoaded(true);
      } catch (err) {
        console.error("Failed to fetch roles:", err);
        if (mountedRef.current) {
          setRolesLoaded(true);
        }
      }
    };
    loadRoles();
  }, []);

  // Memoized filtered roles based on module
  const filteredRoles = useMemo(() => {
    if (!rolesLoaded || availableRoles.length === 0) return [];
    
    const allowedModules = MODULE_MAP[moduleName] || [1];
    return availableRoles.filter(role => 
      allowedModules.includes(role.module) && role.name !== 'Admin'
    );
  }, [availableRoles, rolesLoaded, moduleName]);

  // Memoized role lookup map for O(1) access
  const rolesByUiid = useMemo(() => {
    const map = new Map();
    filteredRoles.forEach(role => map.set(role.uiid, role));
    return map;
  }, [filteredRoles]);

  // Memoized role lookup by name
  const rolesByName = useMemo(() => {
    const map = new Map();
    filteredRoles.forEach(role => map.set(role.name, role));
    return map;
  }, [filteredRoles]);

  // Memoized card click handler
  const handleCardClick = useCallback((title) => {
    setSelectedModal(title);
  }, []);

  // Memoized modal content generator
  const createModalContent = useCallback((roleName, roleId) => (
    <TableIncentive 
      type={roleName} 
      module={moduleName} 
      roleId={roleId}
      availableRoles={availableRoles}
      rolesLoaded={rolesLoaded}
    />
  ), [moduleName, availableRoles, rolesLoaded]);

  // Optimized member count fetching
  const fetchMemberCounts = useCallback(async () => {
    if (customCards.length === 0 || !rolesLoaded || fetchingCountsRef.current) return;
    
    // Skip if we just auto-detected these counts
    if (countsAreFreshRef.current) {
      countsAreFreshRef.current = false;
      return;
    }

    fetchingCountsRef.current = true;
    try {
      setKpiLoading(true)
      const countPromises = customCards.map(async (card) => {
        const role = rolesByName.get(card.title);
        if (!role) return { name: card.title, count: 0 };

        const filters = {
          module: { type: "NONE", value: moduleName },
          role: { type: "NONE", value: role.uiid },
        };
        
        try {
          const count = await fetchIncentiveCount(filters);
          return { name: card.title, count };
        } catch (err) {
          console.warn(`Failed to fetch count for ${card.title}:`, err);
          return { name: card.title, count: 0 };
        }
      });

      const results = await Promise.all(countPromises);
      
      if (!mountedRef.current) return;

      const counts = {};
      results.forEach(({ name, count }) => {
        counts[name] = count;
      });
      
      setMemberCounts(prev => ({ ...prev, ...counts }));
    } catch (err) {
      console.error("Failed to fetch member counts:", err);
    } finally {
      fetchingCountsRef.current = false;
      setKpiLoading(false)
    }
  }, [customCards, rolesByName, moduleName, rolesLoaded]);

  // Auto-detect roles with active members
  const autoDetectActiveRoles = useCallback(async (rolesToScan) => {
    if (rolesToScan.length === 0 || fetchingCountsRef.current) return;

    fetchingCountsRef.current = true;
    try {
      setKpiLoading(true)
      const results = await Promise.all(rolesToScan.map(async (role) => {
        const filters = {
          module: { type: "NONE", value: moduleName },
          role: { type: "NONE", value: role.uiid },
        };
        try {
          const count = await fetchIncentiveCount(filters);
          return { role, count };
        } catch (err) {
          return { role, count: 0 };
        }
      }));

      if (!mountedRef.current) return;

      const activeRoles = results.filter(r => r.count > 0).map(r => r.role);
      
      if (activeRoles.length > 0) {
        const counts = {};
        results.forEach(r => { counts[r.role.name] = r.count; });
        
        // Single batch update for all related states
        countsAreFreshRef.current = true;
        setMemberCounts(prev => ({ ...prev, ...counts }));
        setCheckedRoleIds(new Set(activeRoles.map(r => r.uiid)));
        setCustomCards(activeRoles.map(role => {
          const roleResult = results.find(res => res.role.uiid === role.uiid);
          return {
            title: role.name,
            value: roleResult?.count || 0,
            backValue: 0,
            onClick: () => handleCardClick(role.name),
            modalContent: createModalContent(role.name, role.uiid),
          };
        }));
      }
    } catch (err) {
      console.error("Failed to auto-detect active roles:", err);
    } finally {
      fetchingCountsRef.current = false;
      setKpiLoading(false)
    }
  }, [moduleName, handleCardClick, createModalContent]);

  // Re-fetch counts whenever custom cards change
  useEffect(() => {
    fetchMemberCounts();
  }, [fetchMemberCounts]);

  // Fetch saved activeRoles from IncentiveSetting and rebuild cards
  useEffect(() => {
    if (!rolesLoaded || filteredRoles.length === 0) return;
    if (hasRestoredRef.current) return;

    const settingsKey = SETTINGS_KEY_MAP[moduleName];
    if (!settingsKey) return;

    const restoreActiveRoles = async () => {
      try {
        const data = await fetchIncentiveSettingsApi(settingsKey);
        if (!mountedRef.current) return;

        let settings = null;
        if (Array.isArray(data)) {
          settings = data[0];
        } else if (data?.data) {
          settings = Array.isArray(data.data) ? data.data[0] : data.data;
        } else {
          settings = data;
        }

        if (!settings) {
          await autoDetectActiveRoles(filteredRoles);
          hasRestoredRef.current = true;
          return;
        }

        settingUiidRef.current = settings.uiid;

        const rawActiveRoles = settings.value?.activeRoles;
        if (!rawActiveRoles || (Array.isArray(rawActiveRoles) && rawActiveRoles.length === 0)) {
          await autoDetectActiveRoles(filteredRoles);
          hasRestoredRef.current = true;
          return;
        }

        // Handle MongoDB Mixed field array/object conversion
        const savedRoleIds = Array.isArray(rawActiveRoles) 
          ? rawActiveRoles 
          : Object.values(rawActiveRoles);
        
        if (savedRoleIds.length === 0) {
          await autoDetectActiveRoles(filteredRoles);
          hasRestoredRef.current = true;
          return;
        }

        const restoredCards = savedRoleIds
          .map(uiid => rolesByUiid.get(uiid))
          .filter(Boolean)
          .map(role => ({
            title: role.name,
            value: 0,
            backValue: 0,
            onClick: () => handleCardClick(role.name),
            modalContent: createModalContent(role.name, role.uiid),
          }));

        if (restoredCards.length > 0 && mountedRef.current) {
          setCustomCards(restoredCards);
          setCheckedRoleIds(new Set(savedRoleIds));
        }
        hasRestoredRef.current = true;
      } catch (err) {
        console.warn('Failed to restore active roles from settings:', err);
        // Fallback to auto-detect if fetch fails
        await autoDetectActiveRoles(filteredRoles);
        hasRestoredRef.current = true;
      }
    };

    restoreActiveRoles();
  }, [rolesLoaded, filteredRoles, moduleName, createModalContent, rolesByUiid, handleCardClick]);

  // Memoized all cards - Automatically attach handlers if missing
  const allCards = useMemo(() => {
    const mappedInitial = initialCards.map(card => {
      const alreadyHasHandler = !!card.onClick && !!card.modalContent;
      if (alreadyHasHandler) return card;

      // Try to find role-based metadata if handlers are missing
      const role = rolesByName.get(card.title);
      if (role) {
        return {
          ...card,
          onClick: () => handleCardClick(role.name),
          modalContent: createModalContent(role.name, role.uiid)
        };
      }
      
      // Fallback: attach click handler at least if title matches
      return {
        ...card,
        onClick: () => handleCardClick(card.title)
      };
    });

    return [...mappedInitial, ...customCards];
  }, [initialCards, customCards, rolesByName, handleCardClick, createModalContent]);

  // Memoized current modal content
  const currentModalContent = useMemo(() => {
    const card = allCards.find(item => item.title === selectedModal);
    if (!card?.modalContent) return null;

    if (React.isValidElement(card.modalContent)) {
      return React.cloneElement(card.modalContent, { 
        availableRoles, 
        rolesLoaded,
        ref: tableRef,
        onPanelToggle: setIsAddMemberPanelOpen
      });
    }
    return card.modalContent;
  }, [allCards, selectedModal, availableRoles, rolesLoaded]);

  // Memoized existing card role IDs
  const existingCardRoleIds = useMemo(() => {
    const ids = new Set();
    customCards.forEach(card => {
      const role = rolesByName.get(card.title);
      if (role) ids.add(role.uiid);
    });
    return ids;
  }, [customCards, rolesByName]);

  // Optimized handlers
  const handleOpenAddCategoryModal = useCallback(() => {
    setCheckedRoleIds(existingCardRoleIds);
    setShowAddCategoryModal(true);
  }, [existingCardRoleIds]);

  const handleCloseAddCategoryModal = useCallback(() => {
    setShowAddCategoryModal(false);
    setCheckedRoleIds(new Set());
  }, []);

  const handleToggleRole = useCallback((roleUiid) => {
    setCheckedRoleIds(prev => {
      const next = new Set(prev);
      if (next.has(roleUiid)) {
        next.delete(roleUiid);
      } else {
        next.add(roleUiid);
      }
      return next;
    });
  }, []);

  const handleSaveRoles = useCallback(async () => {
    const selectedRoles = Array.from(checkedRoleIds)
      .map(uiid => rolesByUiid.get(uiid))
      .filter(Boolean);

    const newCards = selectedRoles.map(role => ({
      title: role.name,
      value: 0,
      backValue: 0,
      onClick: () => handleCardClick(role.name),
      modalContent: createModalContent(role.name, role.uiid),
    }));

    setCustomCards(newCards);
    handleCloseAddCategoryModal();

    // Persist to IncentiveSetting
    const activeRoleIds = selectedRoles.map(r => r.uiid);
    if (settingUiidRef.current) {
      try {
        await updateActiveRoles(settingUiidRef.current, activeRoleIds);
      } catch (err) {
        console.warn('Failed to save active roles:', err);
      }
    }
  }, [checkedRoleIds, rolesByUiid, createModalContent, handleCloseAddCategoryModal, handleCardClick]);

  const handleCloseDetailsModal = useCallback(() => {
    setSelectedModal(null);
    setIsAddMemberPanelOpen(false);
    // Refetch counts since user may have added/removed members
    fetchMemberCounts();
  }, [fetchMemberCounts]);

  const handleBulkSaveClick = useCallback(async () => {
    if (!tableRef.current) return;
    
    setIsBulkSaving(true);
    try {
      const success = await tableRef.current.handleBulkSave();
      if (success) {
        if (isAddMemberPanelOpen) {
          tableRef.current.toggleAddMemberPanel();
        }
        fetchMemberCounts();
      }
    } catch (err) {
      console.error("Bulk save click handler failed:", err);
      showToast("An unexpected error occurred", "error");
    } finally {
      setIsBulkSaving(false);
    }
  }, [fetchMemberCounts, isAddMemberPanelOpen]);

  // Memoized loading state
  const isLoadingRoles = useMemo(() => 
    !rolesLoaded,
    [rolesLoaded]
  );

  const topScrollRef = React.useRef(null);

  React.useEffect(() => {
    const handleTopScroll = (e) => {
      const tableWrapper = document.querySelector(".tableWrapper");
      if (tableWrapper) {
        tableWrapper.scrollLeft = e.target.scrollLeft;
      }
    };

    const handleTableScroll = (e) => {
      if (topScrollRef.current) {
        topScrollRef.current.scrollLeft = e.target.scrollLeft;
      }
    };

    const topScroll = topScrollRef.current;
    if (topScroll) {
      topScroll.addEventListener("scroll", handleTopScroll);
    }

    const interval = setInterval(() => {
      const tableWrapper = document.querySelector(".tableWrapper");
      if (tableWrapper && topScrollRef.current) {
        const dummy = topScrollRef.current.firstChild;
        if (dummy) {
          dummy.style.width = `${tableWrapper.scrollWidth}px`;
        }
        tableWrapper.removeEventListener("scroll", handleTableScroll);
        tableWrapper.addEventListener("scroll", handleTableScroll);
      }
    }, 1000);

    return () => {
      if (topScroll) topScroll.removeEventListener("scroll", handleTopScroll);
      clearInterval(interval);
    };
  }, []);

  return (
    <IncentiveRoleContext.Provider value={{ availableRoles, moduleName }}>
    <div className="w-full flex flex-col gap-2">
      <div className="mt-2.5 pb-4 flex flex-col gap-4">
        <div className="flex justify-end">
          <Button
            title="Manage Roles"
            icon="settings"
            className="!py-1.5 !px-3 !text-[13px]"
            onClick={handleOpenAddCategoryModal}
            disabled={isLoadingRoles}
          />
        </div>
        
        <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] md:grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-4 w-full">
          {allCards.map((card, index) => (
            <InfoCardNewAdv
              key={`${card.title}-${index}`}
              title={card.title}
              value={memberCounts[card.title] ?? card.value}
              icon="blinds_closed"
              backValue={card.backValue}
              bgConfig={{ className: "rounded-xl" }}
              layout="option2"
              onClick={card.onClick}
              isLoading={kpiLoading}
            />
          ))}
        </div>
      </div>

      <div className="w-full flex flex-col">
        <NewTab
          tabs={tabs}
          color="black"
          variant="outlined"
          mode="url"
          tabId={tabId}
        />
      </div>

      {/* Manage Roles Modal */}
      <Modal
        isOpen={showAddCategoryModal}
        onClose={handleCloseAddCategoryModal}
        title="Manage Roles"
        width="!w-full md:max-w-md"
        showCloseButton={false}
        bodyClassName="p-4 flex-1"
      >
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">Check roles to add cards, uncheck to remove.</p>
            <div className="flex gap-2">
              <button 
                className="text-[11px] text-orange-600 hover:underline"
                onClick={() => setCheckedRoleIds(new Set(filteredRoles.map(r => r.uiid)))}
              >
                Select All
              </button>
              <span className="text-gray-300">|</span>
              <button 
                className="text-[11px] text-gray-500 hover:underline"
                onClick={() => setCheckedRoleIds(new Set())}
              >
                Deselect All
              </button>
            </div>
          </div>
          
          <div className="flex flex-col gap-1 max-h-[40vh] overflow-y-auto">
            {filteredRoles.length === 0 ? (
              <div className="text-sm text-gray-400 text-center py-4">
                {isLoadingRoles ? "Loading roles..." : "No roles available"}
              </div>
            ) : (
              filteredRoles.map(role => (
                <div
                  key={role.uiid}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                  onClick={() => handleToggleRole(role.uiid)}
                >
                  <input
                    type="checkbox"
                    className="accent-orange-500 w-4 h-4 cursor-pointer"
                    checked={checkedRoleIds.has(role.uiid)}
                    onChange={() => {}}
                    readOnly
                  />
                  <span className="text-sm font-medium text-gray-700">{role.name}</span>
                </div>
              ))
            )}
          </div>
          
          <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
            <Button
              title="Cancel"
              variant="outlined"
              onClick={handleCloseAddCategoryModal}
            />
            <Button 
              title="Save" 
              onClick={handleSaveRoles}
            />
          </div>
        </div>
      </Modal>

      {/* Details Modal */}
      <Modal
        isOpen={!!selectedModal}
        onClose={handleCloseDetailsModal}
        title={`${selectedModal || ""} Details`}
        showCloseButton={false}
        width="!w-full md:!w-[90vw] lg:!w-[70vw]"
        height="!h-[80vh]"
        bodyClassName="p-4 flex flex-col h-full !overflow-hidden"
      >
        <div className="flex flex-col h-full min-h-0">
          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            {currentModalContent}
          </div>
          
          <div className="flex justify-end mt-4 flex-shrink-0 gap-3">
            <Button
              title="Close"
              variant="outlined"
              onClick={handleCloseDetailsModal}
              className="min-w-[100px]"
            />
            <Button
              title={isBulkSaving ? "Saving..." : "Save"}
              className="bg-orange-500 text-white min-w-[100px]"
              onClick={handleBulkSaveClick}
              disabled={isBulkSaving}
              icon={isBulkSaving ? "sync" : "save"}
            />
          </div>
        </div>
      </Modal>
    </div>
    </IncentiveRoleContext.Provider>
  );
};

export default React.memo(IncentiveModuleLayout);
